/**
 * Client half of the automatic-logoff control (WS8, HIPAA §164.312(a)(2)(iii)).
 *
 * The backend is the enforcement point — it kills the session whether or not
 * this hook runs. This hook exists so the user gets *warned* first instead of
 * being silently bounced to a sign-in page mid-sentence, and so genuine
 * activity actually reaches the server (a mouse moving over a page that only
 * polls in the background is invisible to the backend by design).
 *
 * Design notes worth keeping:
 *
 * • **Absolute deadlines, never a decrementing counter.** `setInterval` is
 *   throttled in a background tab and suspended outright on mobile, so a
 *   ticking counter drifts arbitrarily. Everything here is recomputed from
 *   epoch-millisecond deadlines on each tick *and* on `visibilitychange`,
 *   which is what lets us leave TanStack's `refetchOnWindowFocus: false`
 *   alone: returning to a backgrounded tab re-evaluates the real elapsed
 *   time immediately rather than waiting for a query to 401.
 *
 * • **Clock skew is corrected.** The server sends `server_time` alongside its
 *   deadlines; we offset by the difference rather than assuming the two
 *   clocks agree.
 *
 * • **Heartbeat throttle must stay below the warning lead.** The server's
 *   idea of "last activity" lags this hook's by at most one throttle
 *   interval. If that lag exceeded the warning lead, the server could expire
 *   the session while the modal still showed time remaining — the exact
 *   failure the modal exists to prevent. See HEARTBEAT_THROTTLE_MS.
 *
 * • **Passive activity does not dismiss the warning.** Once the modal is up
 *   the countdown is only stopped by an explicit choice. Otherwise nudging
 *   the mouse while reaching for the trackpad would silently re-arm a
 *   session the user was told was ending, and the dialog would flicker.
 *
 * • **The absolute cap cannot be extended**, so when that is the nearer
 *   deadline the hook reports `reason: 'absolute'` and `canExtend: false`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  sendSessionHeartbeat,
  useSessionTimeoutConfig,
  type SessionTimeout,
} from '../api/session'

export type IdleTimeoutState = 'disabled' | 'active' | 'warning' | 'expired'
export type IdleTimeoutReason = 'idle' | 'absolute'

export interface IdleTimeoutResult {
  state: IdleTimeoutState
  /** Which deadline is driving the current warning/expiry. */
  reason: IdleTimeoutReason
  /** Whole seconds until sign-out; 0 once expired. */
  secondsRemaining: number
  /** Length of the warning window, i.e. the denominator for a progress bar. */
  warningSeconds: number
  /** False when the absolute cap is what's expiring — nothing can extend it. */
  canExtend: boolean
  /** Send a heartbeat and re-arm the idle window. No-op when !canExtend. */
  extend: () => void
  /** Sign out now, through the real WorkOS logout. */
  endSession: () => void
}

/**
 * Leading-edge heartbeat interval. MUST be strictly less than the server's
 * `warning_seconds`, and we clamp to guarantee it: the server's
 * `last_activity_at` trails this hook's by at most this much, so a larger
 * value would let the backend expire a session whose modal still showed
 * time on the clock.
 */
const HEARTBEAT_THROTTLE_CEILING_MS = 60_000
const HEARTBEAT_THROTTLE_FLOOR_MS = 10_000

const TICK_MS = 1_000

/** Passive signals that count as "someone is at this workstation". `scroll`
 *  and `mousemove` are the noisy ones; the leading-edge throttle on the
 *  heartbeat is what keeps them from becoming a request storm. */
const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
  'mousemove',
  'scroll',
] as const

/** Navigate to the real sign-out. `/logout` is POST-only on the backend (it
 *  clears the Starlette session, deletes the sealed cookie and hands off to
 *  AuthKit's hosted logout), so we submit a throwaway form rather than
 *  assigning `location.href` — a GET would fall through to the SPA. */
export function submitLogout(): void {
  if (typeof document === 'undefined') return
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = '/logout'
  form.style.display = 'none'
  document.body.appendChild(form)
  form.submit()
}

interface Deadlines {
  /** Local-clock epoch ms. */
  idleAt: number
  absoluteAt: number
}

export function useIdleTimeout(): IdleTimeoutResult {
  const { data: config } = useSessionTimeoutConfig()

  // Local-clock epoch ms of the last activity we observed. 0 until the
  // server tells us where the idle window actually stands — seeding it with
  // `Date.now()` would both be an impure render (react-hooks/purity) and
  // pretend the user just acted when they may have been away for 19 minutes.
  const lastActivityRef = useRef<number>(0)
  const lastHeartbeatRef = useRef<number>(0)
  // Server-provided, skew-corrected. Only the heartbeat response moves it,
  // and only ever forward by the amount the server itself reports.
  const absoluteAtRef = useRef<number | null>(null)
  const skewMsRef = useRef<number>(0)
  const stateRef = useRef<IdleTimeoutState>('disabled')

  const [state, setState] = useState<IdleTimeoutState>('disabled')
  const [reason, setReason] = useState<IdleTimeoutReason>('idle')
  const [secondsRemaining, setSecondsRemaining] = useState(0)

  const enabled = Boolean(
    config?.enforced && config.idle_timeout_seconds > 0
  )

  const idleMs = (config?.idle_timeout_seconds ?? 0) * 1000
  const warningMs = Math.max(0, (config?.warning_seconds ?? 0) * 1000)

  const heartbeatThrottleMs = useMemo(() => {
    // Strictly below the warning lead (half of it), then clamped so we
    // neither spam the backend nor drift past the guarantee above.
    const half = warningMs > 0 ? warningMs / 2 : HEARTBEAT_THROTTLE_CEILING_MS
    return Math.min(
      HEARTBEAT_THROTTLE_CEILING_MS,
      Math.max(HEARTBEAT_THROTTLE_FLOOR_MS, half)
    )
  }, [warningMs])

  /** Fold a server payload into the local refs, correcting for clock skew. */
  const applyServerState = useCallback((payload: SessionTimeout) => {
    const nowLocal = Date.now()
    skewMsRef.current = payload.server_time * 1000 - nowLocal
    const toLocal = (epochSeconds: number) =>
      epochSeconds * 1000 - skewMsRef.current
    lastActivityRef.current = toLocal(payload.last_activity_at)
    absoluteAtRef.current =
      payload.absolute_expires_at !== null
        ? toLocal(payload.absolute_expires_at)
        : null
  }, [])

  const deadlines = useCallback((): Deadlines => {
    return {
      idleAt: lastActivityRef.current + idleMs,
      absoluteAt: absoluteAtRef.current ?? Number.POSITIVE_INFINITY,
    }
  }, [idleMs])

  const heartbeat = useCallback(() => {
    lastHeartbeatRef.current = Date.now()
    void sendSessionHeartbeat()
      .then(applyServerState)
      .catch(() => {
        // A 401 has already sent the browser through the sign-in redirect
        // inside apiFetch. Anything else is transient — the next activity
        // event retries, and the server stays the enforcement point either
        // way, so swallowing here cannot extend a session that should die.
      })
  }, [applyServerState])

  // A page load is unambiguously a human acting, but the request that
  // carried it is an ordinary GET, which the backend deliberately does not
  // count as activity (that rule is what stops background polling from
  // keeping an abandoned workstation alive). So announce it explicitly,
  // once, on the first successful config read — otherwise a user who just
  // navigated here could inherit a nearly-exhausted idle window.
  const didInitialBeatRef = useRef(false)
  useEffect(() => {
    if (!config) return
    applyServerState(config)
    if (config.enforced && !didInitialBeatRef.current) {
      didInitialBeatRef.current = true
      heartbeat()
    }
  }, [config, applyServerState, heartbeat])

  /** Recompute state from the deadlines. Cheap, idempotent, safe to call from
   *  a timer or a visibility change. */
  const evaluate = useCallback(() => {
    // Nothing to evaluate until the server has told us where the idle window
    // stands; a 0 anchor would read as "expired 20 minutes ago".
    if (!enabled || lastActivityRef.current === 0) return
    const now = Date.now()
    const { idleAt, absoluteAt } = deadlines()
    const nextReason: IdleTimeoutReason = absoluteAt <= idleAt ? 'absolute' : 'idle'
    const deadline = Math.min(idleAt, absoluteAt)
    const remainingMs = deadline - now

    let nextState: IdleTimeoutState
    if (remainingMs <= 0) nextState = 'expired'
    else if (remainingMs <= warningMs) nextState = 'warning'
    else nextState = 'active'

    stateRef.current = nextState
    setState(nextState)
    setReason(nextReason)
    // Only publish a countdown while one is on screen. React bails out of a
    // re-render when a setState value is unchanged, so pinning this to 0
    // during the (long) active phase keeps the 1s tick free instead of
    // re-rendering the shell once a second all day.
    setSecondsRemaining(
      nextState === 'active' ? 0 : Math.max(0, Math.ceil(remainingMs / 1000))
    )
  }, [enabled, deadlines, warningMs])

  // ── Activity listeners ───────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return
    const onActivity = () => {
      // While the warning is up the countdown may only be stopped by an
      // explicit choice — see the module docstring.
      if (stateRef.current !== 'active') return
      const now = Date.now()
      lastActivityRef.current = now
      if (now - lastHeartbeatRef.current >= heartbeatThrottleMs) {
        heartbeat()
      }
    }
    // Capture phase: `scroll` does not bubble, and the app's scroll
    // container is <main> inside AppShell, so a bubble-phase window listener
    // would never see the most common activity signal in the whole app.
    const opts: AddEventListenerOptions = { passive: true, capture: true }
    for (const name of ACTIVITY_EVENTS) {
      window.addEventListener(name, onActivity, opts)
    }
    return () => {
      for (const name of ACTIVITY_EVENTS) {
        window.removeEventListener(name, onActivity, opts)
      }
    }
  }, [enabled, heartbeat, heartbeatThrottleMs])

  // ── Tick + visibility ────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return
    // Kick off on the next macrotask rather than synchronously in the effect
    // body: calling setState inline here trips react-hooks/set-state-in-effect
    // and would cascade a render before paint for no benefit. Semantically
    // identical — this just runs the first evaluation ~immediately instead of
    // waiting a full tick.
    const kickoff = window.setTimeout(evaluate, 0)
    const id = window.setInterval(evaluate, TICK_MS)
    // Timers are throttled (mobile: suspended) in a background tab, so the
    // interval alone would under-report elapsed idle time. Re-evaluating the
    // moment the tab is shown closes that gap without turning on
    // refetchOnWindowFocus for every query in the app.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') evaluate()
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onVisibility)
    return () => {
      window.clearTimeout(kickoff)
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onVisibility)
    }
  }, [enabled, evaluate])

  // ── Expiry ───────────────────────────────────────────────────────────
  const expiredRef = useRef(false)
  useEffect(() => {
    if (state !== 'expired' || expiredRef.current) return
    expiredRef.current = true
    submitLogout()
  }, [state])

  const canExtend = reason === 'idle'

  const extend = useCallback(() => {
    if (!canExtend) return
    const now = Date.now()
    lastActivityRef.current = now
    stateRef.current = 'active'
    setState('active')
    heartbeat()
  }, [canExtend, heartbeat])

  return {
    state: enabled ? state : 'disabled',
    reason,
    secondsRemaining,
    warningSeconds: config?.warning_seconds ?? 0,
    canExtend,
    extend,
    endSession: submitLogout,
  }
}
